/** Image picker and upload widget backed by useUpload. Shows compression/progress states and preview when done. */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Camera, Loader2, X } from 'lucide-react'
import { useUpload } from '@/hooks'
import { MediaPickerSheet } from '@/components/common'

interface ImageUploadProps {
  onUploaded: (url: string) => void
  onRemoved: () => void
  currentUrl?: string | null
  type?: 'post' | 'avatar'
}

export function ImageUpload({ onUploaded, onRemoved, currentUrl, type = 'post' }: ImageUploadProps) {
  const { t } = useTranslation()
  const [pickerOpen, setPickerOpen] = useState(false)
  const { state, progress, error, upload, reset } = useUpload(type)

  const handleFileSelected = async (file: File) => {
    const url = await upload(file)
    if (url) onUploaded(url)
  }

  const handleRemove = () => {
    reset()
    onRemoved()
  }

  // Done state (image shown)
  if (state === 'done' || currentUrl) {
    const displayUrl = currentUrl ?? ''
    return (
      <div style={{ position: 'relative', marginTop: 12 }}>
        <img
          src={displayUrl}
          alt="Upload preview"
          style={{
            width: '100%',
            maxHeight: 340,
            objectFit: 'contain',
            borderRadius: 'var(--radius-md)',
            display: 'block',
            border: '1px solid var(--border)',
            background: 'var(--bg-sunk)',
          }}
        />
        <button
          type="button"
          aria-label="Remove image"
          onClick={handleRemove}
          style={{
            position: 'absolute',
            top: 8,
            insetInlineEnd: 8,
            width: 28,
            height: 28,
            borderRadius: '50%',
            border: 'none',
            background: 'rgba(8,10,15,0.65)',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={14} />
        </button>
      </div>
    )
  }

  // Compressing state
  if (state === 'compressing') {
    return (
      <div
        style={{
          marginTop: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          color: 'var(--text-muted)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-xs)',
        }}
      >
        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
        {t('upload.optimizing')}
      </div>
    )
  }

  // Uploading state
  if (state === 'uploading') {
    return (
      <div style={{ marginTop: 12 }}>
        <div
          style={{
            height: 6,
            borderRadius: 3,
            background: 'var(--border)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${progress}%`,
              background: 'var(--brand)',
              borderRadius: 3,
              transition: 'width 0.1s linear',
            }}
          />
        </div>
        <p
          style={{
            margin: '4px 0 0',
            fontSize: 'var(--fs-xs)',
            color: 'var(--text-muted)',
            fontFamily: 'var(--font-sans)',
          }}
        >
          {t('upload.uploading', { percent: progress })}
        </p>
      </div>
    )
  }

  // Error state
  if (state === 'error') {
    return (
      <div style={{ marginTop: 12 }}>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--fs-xs)',
            color: 'var(--danger)',
            fontFamily: 'var(--font-sans)',
          }}
        >
          {error}
        </p>
        <button
          type="button"
          onClick={reset}
          style={{
            marginTop: 6,
            padding: '6px 12px',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--surface-2)',
            color: 'var(--text-muted)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-xs)',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    )
  }

  // Idle state
  return (
    <div style={{ marginTop: 12 }}>
      <MediaPickerSheet
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onFileSelected={handleFileSelected}
        capture="environment"
      />
      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 16px',
          borderRadius: 'var(--radius-md)',
          border: '1.5px dashed var(--border)',
          background: 'none',
          cursor: 'pointer',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-sm)',
          color: 'var(--text-muted)',
        }}
      >
        <Camera size={20} />
        {t('post.add_photo')}
      </button>
    </div>
  )
}
